import { decide, isEntitled, type PatientResponse } from '@emr/shared';
import { useQuery } from '@tanstack/react-query';
import { Tabs } from 'radix-ui';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { getPatient } from '../../api/patients.ts';
import { useGrants, useSession } from '../../auth/sessionContext.ts';
import { LoadingNotice } from '../../components/AsyncState.tsx';
import { GatedRoute } from '../../components/GatedRoute.tsx';
import { DetailRow, Panel } from '../../components/primitives.tsx';
import { formatCalendarDate } from '../../lib/dateFormat.ts';
import { KioskTokenPanel } from '../kiosk/KioskTokenPanel.tsx';
import { LicenseScanTokenPanel } from '../kiosk/LicenseScanTokenPanel.tsx';
import { ChartPresenceIndicator } from './ChartPresenceIndicator.tsx';
import { PatientPhoto } from './PatientPhoto.tsx';
import { QueryFailureNotice } from './QueryFailureNotice.tsx';
import { AllergiesTab } from './tabs/AllergiesTab.tsx';
import { EncountersNotesTab } from './tabs/EncountersNotesTab.tsx';
import { ExportTab } from './tabs/ExportTab.tsx';
import { ImmunizationsTab } from './tabs/ImmunizationsTab.tsx';
import { LabsTab } from './tabs/LabsTab.tsx';
import { MedicationsTab } from './tabs/MedicationsTab.tsx';
import { PreventiveCareTab } from './tabs/PreventiveCareTab.tsx';
import { ProblemsTab } from './tabs/ProblemsTab.tsx';
import { VitalsTab } from './tabs/VitalsTab.tsx';

/**
 * The patient chart: demographics, then the clinical sections behind `chart:read`.
 *
 * Two independent gates, not one, matching `server/src/clinical/patientsRoutes.ts`'s own split:
 * `patient:read` (checked by `GatedRoute` below, at the whole-route level) covers demographics —
 * front desk and billing can open this page and see who the patient is with no treatment
 * relationship at all — while `chart:read` (checked inline, around the tabs) covers the clinical
 * content underneath. A role holding the first but not the second sees a real page, not a blank
 * one or a full-page refusal.
 *
 * A *specific* patient's chart can still be refused even to a role holding both capabilities —
 * `evaluateChartAccess`'s care-team/confidentiality decision, surfaced as `chart_access_denied` and
 * rendered by `QueryFailureNotice`, distinct from both capability gates above.
 */
export function PatientChartPage() {
  const { t } = useTranslation('patientChart');
  const { patientId } = useParams<{ patientId: string }>();
  if (patientId === undefined) {
    // Unreachable given `App.tsx`'s own `patients/:patientId` route — asserting that with a thrown
    // error would be worse than rendering nothing for a case the router itself already prevents.
    return null;
  }

  return (
    <GatedRoute title={t('pageTitle')} capability="patient:read" feature={null}>
      <PatientChart patientId={patientId} />
    </GatedRoute>
  );
}

function PatientChart({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChart');
  const grants = useGrants();
  const patientQuery = useQuery({
    queryKey: ['patient', patientId],
    queryFn: ({ signal }) => getPatient(patientId, signal),
  });

  if (patientQuery.isPending) return <LoadingNotice label={t('loading')} />;
  if (patientQuery.isError) {
    return (
      <QueryFailureNotice error={patientQuery.error} onRetry={() => void patientQuery.refetch()} />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PatientHeader patient={patientQuery.data} />
      <RcmLinks patientId={patientId} />
      {/* Kiosk/tablet check-in (`BACKLOG.md` C7) — a front-desk entry point, not clinical chart
          content, so it sits alongside `RcmLinks` rather than inside `ChartTabs` below. */}
      <KioskTokenPanel patientId={patientId} />
      {/* Driver's-license barcode scan (`BACKLOG.md` C19) — the identical front-desk-entry-point
          register as `KioskTokenPanel` immediately above, not clinical chart content. */}
      <LicenseScanTokenPanel patientId={patientId} />
      {decide(grants, 'chart:read') ? (
        <>
          {/* `ChartPresenceIndicator` renders nothing when no one else is on the chart — a `null`
              child contributes no flex gap here, so this line only ever takes up space once there
              is something worth showing. `key={patientId}` forces a clean remount (and therefore a
              fresh `useChartPresence` connection/state) on every distinct patient — see that hook's
              own header for why it does not reset itself internally on a `patientId` change. */}
          <ChartPresenceIndicator patientId={patientId} key={patientId} />
          <ChartTabs patientId={patientId} />
        </>
      ) : (
        <Panel title={t('clinicalChartPanelTitle')}>
          <p className="m-0 text-sm text-ink">
            <Trans
              t={t}
              i18nKey="noChartAccess"
              components={{ code: <code className="font-mono" /> }}
            />
          </p>
        </Panel>
      )}
    </div>
  );
}

/**
 * Billing/administrative-scope links (`BACKLOG.md` R1/R6) — deliberately **not** inside `ChartTabs`
 * below, and not gated on `chart:read`. `authorization.ts`'s `billing` role holds `billing:manage`
 * and `patient:read` but not `chart:read` at all, so a billing-role user (the primary intended user
 * of both Charges and Statement) would never see a link to either if these lived inside the
 * `chart:read`-gated tab row — found live 2026-08-20, verified against the real deployed demo
 * logged in as `billing.golf`: the whole tabs region, and both RCM links inside it, simply never
 * rendered. Always shown regardless of chart:read; each link still gates on its own capability
 * (`mayViewCharges` below), and the route itself re-checks the same capability regardless, since a
 * URL can be typed directly — matching `ChartTabs`' own dermatology/aesthetics-link precedent.
 */
function RcmLinks({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChart');
  const grants = useGrants();
  const mayViewCharges = decide(grants, 'billing:manage');

  return (
    <div className="flex flex-wrap gap-1 border-b border-line pb-2">
      {mayViewCharges ? (
        <Link to={`/patients/${patientId}/rcm/charges`} className={TAB_LINK_CLASS}>
          {t('tabs.charges')}
        </Link>
      ) : null}
      {/* Ordinary chart content, not a specialty module, so always shown regardless of tier —
          matching this link's own prior comment inside ChartTabs before it moved here. */}
      <Link to={`/patients/${patientId}/statement`} className={TAB_LINK_CLASS}>
        {t('tabs.statement')}
      </Link>
    </div>
  );
}

function PatientHeader({ patient }: { readonly patient: PatientResponse }) {
  const { t } = useTranslation('patientChart');
  const address = formatAddress(patient);
  return (
    <Panel>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        {/* `BACKLOG.md` C18 — a small thumbnail for identification, plus an upload/replace control
            gated on its own capability (`patient:write`) inside `PatientPhoto` itself, not here. */}
        <PatientPhoto patientId={patient.id} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="m-0 text-lg font-semibold text-ink">
              {patient.lastName}, {patient.firstName}
            </h2>
            <span className="font-mono text-sm text-ink-muted break-words [overflow-wrap:anywhere]">
              {t('header.mrn', { mrn: patient.mrn })}
            </span>
          </div>
          <dl className="m-0 mt-3">
            <DetailRow label={t('header.dateOfBirth')}>
              {formatCalendarDate(patient.dateOfBirth)}
            </DetailRow>
            <DetailRow label={t('header.administrativeSex')}>{patient.administrativeSex}</DetailRow>
            {patient.genderIdentity !== null ? (
              <DetailRow label={t('header.genderIdentity')}>{patient.genderIdentity}</DetailRow>
            ) : null}
            <DetailRow label={t('header.phone')}>{patient.phone ?? '—'}</DetailRow>
            <DetailRow label={t('header.email')}>{patient.email ?? '—'}</DetailRow>
            <DetailRow label={t('header.address')}>{address ?? '—'}</DetailRow>
          </dl>
        </div>
      </div>
    </Panel>
  );
}

function formatAddress(patient: PatientResponse): string | null {
  const isPresent = (value: string | null): value is string => value !== null && value !== '';
  const lines = [patient.addressLine1, patient.addressLine2].filter(isPresent);
  const cityState = [patient.city, patient.state].filter(isPresent).join(', ');
  const tail = [cityState, patient.postalCode].filter(isPresent).join(' ');
  const parts = [...lines, tail].filter(isPresent);
  return parts.length === 0 ? null : parts.join(' · ');
}

const TAB_TRIGGER_CLASS =
  'rounded-control px-3 py-2 text-sm font-medium text-ink-muted data-[state=active]:bg-nav-active ' +
  'data-[state=active]:text-nav-active-ink hover:bg-raised';

// Styled identically to `TAB_TRIGGER_CLASS` but never carries `data-[state=active]` — these are real
// navigations to a different route, not a Radix tab-panel switch, so there is no "active" state to
// express the way the six in-page tabs above have one.
const TAB_LINK_CLASS =
  'rounded-control px-3 py-2 text-sm font-medium text-ink-muted hover:bg-raised hover:text-ink';

/**
 * Radix `Tabs` rather than hand-rolled `role="tab"` markup — the same reasoning `theme/ThemeMenu.tsx`
 * already gives for `DropdownMenu`: roving focus and the ARIA tab/tabpanel wiring are supplied by the
 * library rather than re-derived here. Client-side tab state, not nested routes, for the six sections
 * below: the task this page was built for lists only `/patients` and `/patients/:patientId` as
 * routes to add, not a route per chart section.
 *
 * Dermatology, aesthetics, and pediatrics are different: each is a genuinely separate route
 * (`/patients/:patientId/dermatology`, `/patients/:patientId/aesthetics`,
 * `/patients/:patientId/pediatrics`), built after this page and gated by its own `GatedRoute`
 * (capability *and* entitlement, re-checked there regardless of what's shown here — this is a
 * convenience link, not the real gate). They render as `Link`s styled to sit in the same row as the
 * six tabs, entitlement-gated the same way `SpecialtyModulesPage.tsx` gates its own catalog rows, so
 * a patient's specialty content is actually reachable from their own chart instead of only by typing
 * the URL directly — found 2026-08-18, live: the module was built and entitled with no link to it
 * from anywhere in the chart UI at all.
 */
function ChartTabs({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChart');
  const session = useSession();
  const dermatologyEntitled = isEntitled(session.tier, 'dermatologyModule');
  const aestheticsEntitled = isEntitled(session.tier, 'aestheticsModule');
  const pediatricsEntitled = isEntitled(session.tier, 'pediatricsModule');

  return (
    <Tabs.Root defaultValue="problems">
      <Tabs.List
        aria-label={t('tabs.ariaLabel')}
        className="flex flex-wrap gap-1 border-b border-line pb-2"
      >
        <Tabs.Trigger value="problems" className={TAB_TRIGGER_CLASS}>
          {t('tabs.problems')}
        </Tabs.Trigger>
        <Tabs.Trigger value="allergies" className={TAB_TRIGGER_CLASS}>
          {t('tabs.allergies')}
        </Tabs.Trigger>
        <Tabs.Trigger value="medications" className={TAB_TRIGGER_CLASS}>
          {t('tabs.medications')}
        </Tabs.Trigger>
        <Tabs.Trigger value="immunizations" className={TAB_TRIGGER_CLASS}>
          {t('tabs.immunizations')}
        </Tabs.Trigger>
        {/* Preventive-care-gap reminders (`BACKLOG.md` C12) — placed beside Immunizations since its
            three rules (annual flu, 10-year Tdap/Td, one-time pneumococcal at 65+) are all evaluated
            against that same immunization history, not a new clinical section unrelated to it. */}
        <Tabs.Trigger value="preventive-care" className={TAB_TRIGGER_CLASS}>
          {t('tabs.preventiveCare')}
        </Tabs.Trigger>
        <Tabs.Trigger value="vitals" className={TAB_TRIGGER_CLASS}>
          {t('tabs.vitals')}
        </Tabs.Trigger>
        <Tabs.Trigger value="labs" className={TAB_TRIGGER_CLASS}>
          {t('tabs.labs')}
        </Tabs.Trigger>
        <Tabs.Trigger value="encounters-notes" className={TAB_TRIGGER_CLASS}>
          {t('tabs.encountersNotes')}
        </Tabs.Trigger>
        <Tabs.Trigger value="export" className={TAB_TRIGGER_CLASS}>
          {t('tabs.export')}
        </Tabs.Trigger>
        {/* Form-template submissions (BACKLOG.md C10b) — a real route, same shape as the
            dermatology/aesthetics/pediatrics links below, not a Radix tab panel, since the fill UI
            needs its own URL (a specific submission or a specific template to fill). No entitlement
            gate, unlike those three: this is core documentation infrastructure, not a tier-gated
            specialty module — see server/src/forms/formSubmissionsRoutes.ts's own header. */}
        <Link to={`/patients/${patientId}/forms`} className={TAB_LINK_CLASS}>
          {t('tabs.forms')}
        </Link>
        {dermatologyEntitled ? (
          <Link to={`/patients/${patientId}/dermatology`} className={TAB_LINK_CLASS}>
            {t('tabs.dermatology')}
          </Link>
        ) : null}
        {aestheticsEntitled ? (
          <Link to={`/patients/${patientId}/aesthetics`} className={TAB_LINK_CLASS}>
            {t('tabs.aesthetics')}
          </Link>
        ) : null}
        {pediatricsEntitled ? (
          <Link to={`/patients/${patientId}/pediatrics`} className={TAB_LINK_CLASS}>
            {t('tabs.pediatrics')}
          </Link>
        ) : null}
      </Tabs.List>

      <Tabs.Content value="problems" className="pt-4">
        <ProblemsTab patientId={patientId} />
      </Tabs.Content>
      <Tabs.Content value="allergies" className="pt-4">
        <AllergiesTab patientId={patientId} />
      </Tabs.Content>
      <Tabs.Content value="medications" className="pt-4">
        <MedicationsTab patientId={patientId} />
      </Tabs.Content>
      <Tabs.Content value="immunizations" className="pt-4">
        <ImmunizationsTab patientId={patientId} />
      </Tabs.Content>
      <Tabs.Content value="preventive-care" className="pt-4">
        <PreventiveCareTab patientId={patientId} />
      </Tabs.Content>
      <Tabs.Content value="vitals" className="pt-4">
        <VitalsTab patientId={patientId} />
      </Tabs.Content>
      <Tabs.Content value="labs" className="pt-4">
        <LabsTab patientId={patientId} />
      </Tabs.Content>
      <Tabs.Content value="encounters-notes" className="pt-4">
        <EncountersNotesTab patientId={patientId} />
      </Tabs.Content>
      <Tabs.Content value="export" className="pt-4">
        <ExportTab patientId={patientId} />
      </Tabs.Content>
    </Tabs.Root>
  );
}
